import { storeActions } from '@/store/actions'
import { createReducer, isAnyOf, UnknownAction } from '@reduxjs/toolkit/react'
import { TimeType } from '@repo/shared/dtos/generic'
import { ChatMessageSender } from '@repo/shared/dtos/message'
import { Language } from '@repo/ui/lang'
import { AsyncData, DataStatus } from '@repo/ui/utils'
import { v4 } from 'uuid'

export type ChatMessage = {
  content: string
  id: string
  sender: ChatMessageSender
  sentAt: TimeType
  streaming: boolean
}

export type ChatSession = {
  beneficiaryId: string
  closedAt?: TimeType
  id: string
  startedAt: TimeType
}

export type Beneficiary = {
  fullName: string
  id: string
  language?: Language
  profile: string
}

export type OrganizationChatSettings = {
  colors: {
    '--color-gradient-start': [number, number, number]
    '--color-gradient-end': [number, number, number]
    '--color-highlight': string
    '--color-primary-default': string
    '--color-primary-50': string
    '--color-primary-100': string
    '--color-primary-200': string
    '--color-primary-300': string
    '--color-primary-400': string
    '--color-primary-500': string
    '--color-primary-600': string
    '--color-primary-700': string
    '--color-primary-800': string
    '--color-primary-900': string
    '--color-primary-950': string
  }
  imageUrl: string
  allowFeedback?: boolean
}
export type OrganizationTexts = {
  beneficiary_app: {
    information_notice: {
      en: string
      fr: string
      es: string
      pt: string
    }
    welcome: {
      en: string
      fr: string
      es: string
      pt: string
    }
  }
}
export type Organization = {
  chatSettings?: OrganizationChatSettings
  id: string
  language: Language
  name: string
  texts?: OrganizationTexts
}

export type Task = {
  archivedAt?: TimeType
  beneficiaryId: string
  cta?: {
    link: string
    name: string
  }
  categories?: string[]
  content: string
  createdAt: TimeType
  dueDate?: TimeType
  durationInHours?: number
  id: string
  title?: string
}

export type BeneficiaryState = {
  beneficiary: AsyncData<Beneficiary>
  chatSessions: AsyncData<ChatSession[]>
  currentSessionId: string | undefined
  isBotThinking: boolean
  messages: AsyncData<ChatMessage[]>
  organization: AsyncData<Organization>
  tasks: AsyncData<Task[]>
}

export const beneficiaryInitialState: BeneficiaryState = {
  beneficiary: DataStatus.Uninitialized,
  chatSessions: DataStatus.Uninitialized,
  currentSessionId: undefined,
  isBotThinking: false,
  messages: DataStatus.Uninitialized,
  organization: DataStatus.Uninitialized,
  tasks: DataStatus.Uninitialized,
}

export const beneficiaryReducer: ((
  state: BeneficiaryState | undefined,
  action: UnknownAction,
) => BeneficiaryState) & {
  getInitialState: () => BeneficiaryState
} = createReducer(beneficiaryInitialState, (builder) => {
  builder
    .addCase(storeActions.initBeneficiary.pending, (state) => {
      state.beneficiary = DataStatus.Loading
      state.chatSessions = DataStatus.Loading
      state.organization = DataStatus.Loading
      state.tasks = DataStatus.Loading
    })
    .addCase(storeActions.initBeneficiary.rejected, (state) => {
      state.beneficiary = DataStatus.Error
      state.chatSessions = DataStatus.Error
      state.organization = DataStatus.Error
      state.tasks = DataStatus.Error
    })
    .addCase(
      storeActions.initBeneficiary.fulfilled,
      (
        state,
        { payload: { beneficiary, chatSessions, organization, tasks } },
      ) => {
        state.beneficiary = beneficiary
        state.chatSessions = chatSessions
        state.organization = organization
        state.tasks = tasks.filter((a) => !a.archivedAt) // Filter out archived tasks
      },
    )

  builder
    .addCase(storeActions.initChatSession.pending, (state) => {
      state.messages = DataStatus.Loading
    })
    .addCase(storeActions.initChatSession.rejected, (state) => {
      state.messages = DataStatus.Error
    })
    .addCase(
      storeActions.initChatSession.fulfilled,
      (
        state,
        {
          meta: {
            arg: { chatSessionId },
          },
          payload: { chatMessages },
        },
      ) => {
        state.isBotThinking = chatMessages.length === 0
        state.messages = chatMessages
        state.currentSessionId = chatSessionId
      },
    )

  builder.addCase(
    storeActions.createChatSession.fulfilled,
    (state, { payload: { chatSession } }) => {
      if (DataStatus.isFulfilled(state.chatSessions))
        state.chatSessions.push(chatSession)
      else if (DataStatus.isUninitialized(state.chatSessions))
        state.chatSessions = [chatSession]
    },
  )

  builder.addCase(
    storeActions.updateBeneficiaryLanguage.fulfilled,
    (state, { payload: { beneficiary } }) => {
      state.beneficiary = beneficiary
    },
  )

  builder.addCase(
    storeActions.markTaskAsDone.fulfilled,
    (state, { payload: { task } }) => {
      if (DataStatus.isFulfilled(state.tasks))
        state.tasks = state.tasks.filter((a) => a.id !== task.id)
    },
  )

  builder.addCase(
    storeActions.sendBeneficiaryMessage,
    (state, { payload: { message } }) => {
      state.isBotThinking = true
      const newMessage = {
        content: message,
        sender: ChatMessageSender.Beneficiary,
        sentAt: new Date().getTime(),
        id: v4(),
      }
      if (DataStatus.isFulfilled(state.messages))
        state.messages.push({ ...newMessage, streaming: false })
      else if (DataStatus.isUninitialized(state.messages))
        state.messages = [{ ...newMessage, streaming: false }]
    },
  )

  builder.addCase(
    storeActions.streamMessage,
    (state, { payload: { chatMessage } }) => {
      state.isBotThinking = chatMessage.sender !== ChatMessageSender.Bot
      if (DataStatus.isFulfilled(state.messages)) {
        const index = state.messages.findIndex((a) => a.id === chatMessage.id)
        const isFound = state.messages[index]
        if (isFound) isFound.content += chatMessage.content
        else state.messages.push(chatMessage)
      } else if (DataStatus.isUninitialized(state.messages)) {
        state.messages = [chatMessage]
      }
    },
  )

  builder.addMatcher(
    isAnyOf(
      storeActions.runTaskHelper.pending,
      storeActions.runTaskHelper.fulfilled,
    ),
    (state) => {
      state.isBotThinking = true
    },
  )
})
